import { useContext } from 'react';
import { styled } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Field, FormikProvider, FormikValues, useFormik } from "formik";
import * as Yup from 'yup';
import Button from "../../../../customs/Button";
import Input from "../../../../customs/FormInput";
import Select from "../../../../customs/SelectInput";
import { NotificationContext } from '../../../../providers/notification';
import apiCall from '../../../../utils/apiCall';
import { reOrderFetchedData } from '../../../../hooks/FetchData';
import { RouteResponse, Route } from '../../../../types';


interface ComponentProps {
    handleClose: () => void;
}

interface Payload {
    Name: string;
    RouteId: number;
}



const CreateScoreType: React.FC<ComponentProps> = ({ handleClose }) => {
    const { showNotification } = useContext(NotificationContext);
    const queryClient = useQueryClient();

    const getAllRoutes = async (): Promise<RouteResponse> => {
        return (await apiCall().get('/Routes/GetAllRoute')).data
    };

    const { data, isLoading, isError, error } = useQuery(['get-all-route'], getAllRoutes, {
        enabled: true,
        keepPreviousData: true,
        retry: 0
    });

    const postScoreType = (data: Payload) => {
        return apiCall().post('/SetUp/CreateUpdateScoreType', data);
    };

    const postScoreTypeMutation = useMutation(['post-score-type'], postScoreType, {retry: 0});

    const postScoreTypeHandler = async (values: FormikValues, resetForm: () => void) => {
        const payload: Payload = {
            Name: values?.name,
            RouteId: values?.route,
        };

        try {
            await postScoreTypeMutation.mutateAsync(payload, {
                onSuccess: () => {
                    showNotification({
                        message: 'Score Type successfully added',
                        type: 'success',
                    });

                    queryClient.refetchQueries(['get-score-type']);
                    resetForm();
                    handleClose();
                }
            })
        } catch (error: any){
            showNotification({
                message: error?.response?.data?.title || error?.message,
                type: 'error',
            });
        }
    };

    const validationSchema = Yup.object().shape({
        name: Yup.string().matches(/^[A-Za-z\s!@#$%^&*()/]+$/, 'Only text is allowed').required('required'),
        route: Yup.string().required('Route name is required')
    });

    const formik = useFormik<FormikValues>({
        initialValues: {
            name: ''
        },
        onSubmit: (values, {resetForm}) => {
            postScoreTypeHandler(values, resetForm);
        },
        validationSchema,
    });

    const routeOption =
    data?.Data && data?.Data?.length > 0 ? (
       reOrderFetchedData(data?.Data).map((route : Route ) => (
                <option value={route?.RouteId} key = {route?.RouteId} >
                    {route?.RouteName}
                </option>
            ))
        ) : (
            <option disabled>
                {isLoading ? 'loading....' : isError ? 'error': ''}
            </option>
    );

    return (
        <FormikProvider value={formik}>
            <form onSubmit={formik.handleSubmit}>
                <FormWrapper>
                    <Field 
                        name='name'
                        type='text'
                        as={Input}
                        label='Name'
                        placeholder='Name'
                    />
                    <Field 
                        name='route'
                        type='text'
                        as={Select}
                        label='Route'
                        options= {routeOption}
                        placeholder='Select'
                    />
                </FormWrapper>
                <ButtonWrapper>
                    <ButtonContainer>
                        <StyledButton onClick={handleClose} text='Cancel'/>
                        <StyledButton 
                            type="submit" 
                            text={postScoreTypeMutation.isLoading ? 'Submitting...' : 'Submit'}
                            disabled={postScoreTypeMutation.isLoading}
                        />
                    </ButtonContainer>
                </ButtonWrapper>
            </form>
        </FormikProvider>
    )
};

export default CreateScoreType;

const FormWrapper = styled('div')`
    margin-inline: 3.5rem;
`

const ButtonWrapper = styled('div')`
    display: flex;
    justify-content: flex-end;
    margin-block: 2.5rem;
    padding-inline: 3.5rem;
`

const ButtonContainer = styled('div')`
    display: flex;
    gap: 2rem;
`

const StyledButton = styled(Button)`
    padding-block: 1rem;
    padding-inline: 2rem;
`

